import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useContext } from "react";
import { getUser, updateUser } from "../../requests/userManagementRequests";
import { getPlatform } from "../../requests/platformManagementRequests";
import authentication from "../../modules/authentication";
import UserContext from "./UserContext";
import { useQuery } from "../../components/Hooks";
import AlertContext from "../socket/SocketContext";
import SocketContext from "../socket/SocketContext";
import _ from "lodash";
import * as qmr from "../../requests/quoteManagementRequests";

const UserContextProvider = ({ children }) => {
  // const { matches: isDarkOS } = window.matchMedia(
  //   "(prefers-color-scheme: dark)"
  // );

  const dm = window.sessionStorage.getItem("darkMode");
  const { username } = authentication.verifyToken() || {};
  const [searchParams] = useSearchParams();
  const productId = searchParams.get("productId") || "";

  const [loggedIn, setLoggedIn] = useState(!!username);
  const [activeUser, setActiveUser] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [queries, setQueries] = useState({});
  const [inFlight, setInFlight] = useState(false);
  const [loggedInUsers, setLoggedInUsers] = useState({});
  const [activeOrderStatus, setActiveOrderStatus] = useState([]);
  const [numOrdersUpdated, setNumOrdersUpdated] = useState(0);
  const [refetchHeader, setRefetchHeader] = useState(false);

  const { showSuccessAlert } = useContext(AlertContext);
  const { userLoggedIn } = useContext(SocketContext);

  const _setQueries = useCallback((data) => {
    if (typeof setQueries === "function") setQueries(data);
  }, []);

  const [darkMode, setDarkMode] = useState(
    productId ? false : dm === "true" ? true : dm === "false" ? false : false
  );

  const updateLoggedInUsers = useCallback(
    (user) => {
      const { username, content } = user || {};
      const { isLoggedIn } = content || {};
      setLoggedInUsers({ ...loggedInUsers, [username]: isLoggedIn });
    },
    [loggedInUsers]
  );

  const { pid } = activeUser || {};

  const {
    data: platform,
    isLoading: platformLoading,
    refetchQuery: refetchPlatform,
  } = useQuery({
    queries,
    setQueries,
    queryID: "platform",
    request: getPlatform,
    queryVariables:
      !loggedIn || activeUser?.roleLevel === 0
        ? { defaultPlatform: true }
        : { id: pid },
    forcedFilters: {},
    skip:
      isLoading ||
      (loggedIn && !pid && activeUser?.roleLevel > 1) ||
      (loggedIn && !activeUser),
  });

  const updateOrderStatus = useCallback(
    (data) => {
      const { activeOrders = [], numItemsUpdated } = data;
      
      setActiveOrderStatus(activeOrders);

      if (activeUser?.roleLevel >= 5 && platform) {
        const { emailAddresses: _emails } = platform;
        const emails = JSON.parse(_emails).split(",");
        activeOrders.forEach((order) => {
          const statusTypes = {
            P: "Planned",
            S: "Stopped",
            O: "Ordered",
            H: "History",
          };
          const subject = `New Update to Your ${
            activeOrders.length > 1 ? "Orders" : "Order"
          }.`;
          const htmlBody = (
            <div>
              {activeOrders.map((order) => {
                return Object.keys(order).map((val) => <p>{val}</p>);
              })}
            </div>
          );
          setNumOrdersUpdated(numItemsUpdated);
          if (_.isArray(emails)) {
            qmr.sendOrderStatusEmails({
              emails,
              subject,
              htmlBody,
            });
          }

          if (showSuccessAlert)
            showSuccessAlert(
              `Order #${order.swiftsellQuoteId} status changed to ${
                statusTypes[order.csiStatus]
              }`
            );
        });
      }
    },
    [activeUser, platform, showSuccessAlert]
  );

  const buildUserObject = useCallback(
    async (user, signal) => {
      if (!user) {
        setLoading(false);
        return;
      }

      if (!activeUser?.id) {
        setLoading(true);

        const userResponse = await getUser({
          username: user,
          signal,
        });

        const { result: currentUser } = userResponse || {};

        if (!currentUser) return console.log("No user");

        setActiveUser(currentUser);
        updateUser({ id: currentUser?.id, input: { isLoggedIn: true } });
        setLoggedIn(true);
        const userQueryObject = {
          data: currentUser,
          refetchQuery: buildUserObject,
          setData: setActiveUser,
          isLoading: false,
        };

        setQueries({ ...queries, [currentUser.username]: userQueryObject });

        userLoggedIn({ user, updateLoggedInUsers, updateOrderStatus });

        setLoading(false);

        return;
      }
    },
    [activeUser, queries, updateLoggedInUsers, updateOrderStatus, userLoggedIn]
  );

  useEffect(() => {
    if (dm) return;
    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", function (e) {
        const isDark = e.matches;
        const loc = window.sessionStorage.getItem("darkMode");
        setDarkMode(loc === "true" ? true : loc === "false" ? false : isDark);
      });
  }, [dm]);

  useEffect(() => {
    const controller = new AbortController();

    if (!isLoading) buildUserObject(username, controller.signal);

    return () => controller.abort();
  }, [buildUserObject, isLoading, username]);

  const loading = useMemo(() => {
    if (isLoading || platformLoading || !platform || (!activeUser && loggedIn))
      return true;
    return false;
  }, [activeUser, isLoading, loggedIn, platform, platformLoading]);

  return (
    <UserContext.Provider
      value={{
        activeUser,
        darkMode,
        setDarkMode,
        isLoading: loading,
        setActiveUser,
        loggedIn,
        setLoggedIn,
        buildUserObject,
        activeOrderStatus,
        setActiveOrderStatus,
        setNumOrdersUpdated,
        numOrdersUpdated,
        username,
        platform,
        queries,
        setQueries: _setQueries,
        inFlight,
        setInFlight,
        loggedInUsers,
        setLoggedInUsers,
        pid,
        setLoading,
        platformLoading,
        refetchPlatform,
        refetchHeader,
        setRefetchHeader,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserContextProvider;
